/**
 * Streamed answers, read from a fetch `Response`.
 *
 * `readLines` yields every complete line however the network split the bytes.
 * `readSseData` yields the payload of every server-sent-events `data:` line;
 * comment lines (`: keep-alive`, OpenRouter's `: OPENROUTER PROCESSING`) and
 * other fields are skipped.
 */

import { ShaderGenerationError } from '../types'
import { abortedError } from './errors'

export async function readLines(
  response: Response,
  signal: AbortSignal,
  onLine: (line: string) => 'stop' | void,
): Promise<void> {
  const reader = response.body?.getReader()
  if (!reader) throw new ShaderGenerationError('The model returned an empty response.', 'NETWORK')
  const decoder = new TextDecoder()
  let buffer = ''
  const stopOnAbort = () => void reader.cancel().catch(() => undefined)
  signal.addEventListener('abort', stopOnAbort)

  // Returns true when the consumer asked to stop.
  const flush = (lines: string[]): boolean => {
    for (const raw of lines) {
      if (onLine(raw.endsWith('\r') ? raw.slice(0, -1) : raw) === 'stop') return true
    }
    return false
  }

  try {
    for (;;) {
      if (signal.aborted) throw abortedError()
      const { done, value } = await reader.read()
      if (signal.aborted) throw abortedError()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''
      if (flush(lines)) return
    }
    buffer += decoder.decode()
    if (buffer) flush(buffer.split('\n'))
  } finally {
    signal.removeEventListener('abort', stopOnAbort)
    reader.releaseLock()
  }
}

export async function readSseData(
  response: Response,
  signal: AbortSignal,
  onData: (data: string) => 'stop' | void,
): Promise<void> {
  await readLines(response, signal, (line) => {
    if (!line.startsWith('data:')) return
    return onData(line.slice(line.startsWith('data: ') ? 6 : 5))
  })
}

interface ChatCompletionChunk {
  choices?: Array<{
    delta?: { content?: string | null }
    message?: { content?: string | null }
    finish_reason?: string | null
  }>
  error?: { message?: string } | string
}

function chunkError(chunk: ChatCompletionChunk): string | null {
  if (!chunk.error) return null
  return typeof chunk.error === 'string' ? chunk.error : chunk.error.message ?? 'The model stopped with an error.'
}

/** The model hit its output limit: the program is cut off, and asking again hits the same limit. */
export function truncatedError(): ShaderGenerationError {
  return new ShaderGenerationError(
    'The model ran out of output tokens before the shader was finished. Pick a model that does not spend its budget on reasoning, or ask for a simpler effect.',
    'TRUNCATED',
  )
}

export function unfinishedStreamError(): ShaderGenerationError {
  return new ShaderGenerationError('The connection ended before the shader was finished. Try again.', 'NETWORK')
}

/** Throws for a finish reason that means the answer is not a complete program. */
function checkFinishReason(reason: string | null | undefined): void {
  if (reason === 'length') throw truncatedError()
  if (reason === 'error') throw new ShaderGenerationError('The model stopped with an error before the shader was finished.', 'UNKNOWN')
  if (reason === 'content_filter') throw new ShaderGenerationError("The provider's content filter stopped the answer.", 'UNKNOWN')
}

/**
 * Reads an OpenAI-compatible chat-completions stream (OpenRouter, xAI) and
 * returns the full text, reporting it as it grows. A server that ignored
 * `stream: true` and answered with one JSON body is read too.
 *
 * Only a finished answer is returned: a `length` finish throws `TRUNCATED`, and
 * a stream that closes with neither `[DONE]` nor a finish reason throws `NETWORK`.
 */
export async function readChatCompletionStream(
  response: Response,
  signal: AbortSignal,
  onText?: (textSoFar: string) => void,
): Promise<string> {
  if (response.headers.get('content-type')?.includes('application/json')) {
    const body = (await response.json()) as ChatCompletionChunk
    const error = chunkError(body)
    if (error) throw new ShaderGenerationError(error, 'UNKNOWN')
    checkFinishReason(body.choices?.[0]?.finish_reason)
    const text = body.choices?.[0]?.message?.content ?? ''
    onText?.(text)
    return text
  }

  let text = ''
  let finished = false
  await readSseData(response, signal, (data) => {
    const payload = data.trim()
    if (payload === '[DONE]') {
      finished = true
      return 'stop'
    }
    if (!payload) return
    let chunk: ChatCompletionChunk
    try {
      chunk = JSON.parse(payload) as ChatCompletionChunk
    } catch {
      return
    }
    const error = chunkError(chunk)
    if (error) throw new ShaderGenerationError(error, 'UNKNOWN')
    const choice = chunk.choices?.[0]
    const delta = choice?.delta?.content
    if (delta) {
      text += delta
      onText?.(text)
    }
    if (choice?.finish_reason) {
      checkFinishReason(choice.finish_reason)
      finished = true
    }
  })
  if (!finished) throw unfinishedStreamError()
  return text
}
